/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 *
 * countyGeo :: lazy access to the county geometry bundles that ship in the
 * top-level `map-core/counties/` package (the shared US-map renderer). Both
 * System Settings (county entry UI) and the distribution-map module (rendering)
 * read counties through this one accessor, so the map-core dependency is
 * declared in a single shared place instead of being reached into from two
 * modules.
 *
 * - manifest.json: { viewBox, states: { <fips>: { iso, name, bbox, countyCount } } }
 * - <fips>.json:   { stateFips, iso, name, bbox, counties: { <fips5>: { d, lp, name } } }
 *
 * FIPS identity note: the app keys states by ISO ('US-CA'); the bundles + the
 * county FIPS are numeric ('06', '06037'). Use isoToFips / fipsToIso to cross.
 */

// Lazy singletons. Vite code-splits each bundle into its own chunk, so a state's
// counties only download when first requested.
let _manifestPromise = null;
const _bundleCache = new Map();

export async function getCountyManifest() {
  if (!_manifestPromise) {
    _manifestPromise = import("../../../../map-core/counties/manifest.json").then(
      (m) => m.default ?? m
    );
  }
  return _manifestPromise;
}

// Load one state's county bundle by 2-digit state FIPS ('06'). Cached.
export async function loadCountyBundle(stateFips) {
  const fips = String(stateFips);
  if (!/^[0-9]{2}$/.test(fips)) throw new Error(`Bad state FIPS: ${stateFips}`);
  if (!_bundleCache.has(fips)) {
    _bundleCache.set(
      fips,
      import(`../../../../map-core/counties/${fips}.json`).then((m) => m.default ?? m)
    );
  }
  return _bundleCache.get(fips);
}

// ── ISO <-> state FIPS, derived once from the manifest ───────────────────────
let _indexPromise = null;
async function stateIndex() {
  if (!_indexPromise) {
    _indexPromise = getCountyManifest().then((manifest) => {
      const isoToFips = {};
      const fipsToIso = {};
      const fipsToName = {};
      const meta = {};
      const nameToFips = {}; // lowercased full name -> fips
      const abbrToFips = {}; // lowercased 2-letter (from ISO) -> fips
      for (const [fips, s] of Object.entries(manifest.states || {})) {
        isoToFips[s.iso] = fips;
        fipsToIso[fips] = s.iso;
        fipsToName[fips] = s.name;
        meta[fips] = { iso: s.iso, name: s.name, bbox: s.bbox, countyCount: s.countyCount };
        if (s.name) nameToFips[s.name.toLowerCase()] = fips;
        if (s.iso) abbrToFips[s.iso.slice(3).toLowerCase()] = fips;
      }
      return {
        isoToFips, fipsToIso, fipsToName, meta, nameToFips, abbrToFips,
        stateFipsSet: new Set(Object.keys(manifest.states || {})),
      };
    });
  }
  return _indexPromise;
}

// Resolve a System Settings free-text state (full name, 2-letter abbr, or ISO)
// to its 2-digit state FIPS. Returns null if it can't be matched.
export async function resolveStateToFips(value) {
  if (!value) return null;
  const v = String(value).trim();
  const { isoToFips, nameToFips, abbrToFips } = await stateIndex();
  if (isoToFips[v.toUpperCase()]) return isoToFips[v.toUpperCase()];
  const lower = v.toLowerCase();
  return nameToFips[lower] ?? abbrToFips[lower] ?? null;
}

// { iso, name, bbox, countyCount } for a 2-digit state FIPS, or null.
export async function stateMeta(stateFips) {
  const { meta } = await stateIndex();
  return meta[String(stateFips)] || null;
}

// Resolve a free-text state to its ISO code ('US-CA'), or null. Convenience for
// callers that key by ISO (e.g. sales_state_modes).
export async function resolveStateToIso(value) {
  const fips = await resolveStateToFips(value);
  if (!fips) return null;
  const { fipsToIso } = await stateIndex();
  return fipsToIso[fips] || null;
}

export async function isoToStateFips(iso) {
  const { isoToFips } = await stateIndex();
  return isoToFips[iso] || null;
}

export async function stateFipsToIso(fips) {
  const { fipsToIso } = await stateIndex();
  return fipsToIso[String(fips)] || null;
}

// Validate a 5-digit county FIPS: format + its 2-digit state prefix is a real
// state in the manifest. (The full per-county set isn't loaded here to avoid
// pulling every bundle; the entry UI only offers real counties, and map-public
// re-validates server-side.)
export async function isValidCountyFips(countyFips) {
  const v = String(countyFips ?? "");
  if (!/^[0-9]{5}$/.test(v)) return false;
  const { stateFipsSet } = await stateIndex();
  return stateFipsSet.has(v.slice(0, 2));
}
